import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import React from "react";

const SPREADSHEET_EXTENSIONS = new Set(["csv", "xls", "xlsx", "xlsm", "ods"]);
const WORD_EXTENSIONS = new Set(["doc", "docx"]);
const PDF_EXTENSIONS = new Set(["pdf"]);
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "3gp", "mov", "webm", "mkv"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "ogg", "aac", "m4a", "opus"]);

const GENERIC_MEDIA_TEXT = new Set([
  "document",
  "image",
  "video",
  "audio",
  "sticker",
  "text",
]);

const SPREADSHEET_MIMES = new Set([
  "text/csv",
  "text/comma-separated-values",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
]);

const ENCODED_STORAGE_NAME = /^\d{10,}_[A-Za-z0-9]+\.[A-Za-z0-9]+$/i;

export const getFileExtension = (fileName) => {
  if (!fileName || !String(fileName).includes(".")) {
    return "";
  }
  return String(fileName).split(".").pop().toLowerCase();
};

export const isEncodedStorageFileName = (fileName) => {
  const name = String(fileName || "").trim();
  if (!name) {
    return false;
  }
  return ENCODED_STORAGE_NAME.test(name);
};

export const getExtensionDisplayLabel = (fileName) => {
  const ext = getFileExtension(fileName);
  return ext ? `.${ext}` : "";
};

export const getDisplayFileLabel = (fileName, typeName) => {
  if (!fileName) {
    return typeName;
  }
  if (isEncodedStorageFileName(fileName)) {
    return getExtensionDisplayLabel(fileName) || typeName;
  }
  return fileName;
};

const looksLikeFileName = (value) => {
  const text = String(value || "").trim();
  if (!text || GENERIC_MEDIA_TEXT.has(text.toLowerCase())) {
    return false;
  }
  if (/^https?:\/\//i.test(text)) {
    return false;
  }
  return text.includes(".") && text.length < 255;
};

export const fileNameFromUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const path = new URL(url, window.location.origin).pathname;
    const baseName = decodeURIComponent(path.split("/").pop() || "");
    return baseName || null;
  } catch {
    const fallback = String(url).split("/").pop() || "";
    return fallback ? decodeURIComponent(fallback.split("?")[0]) : null;
  }
};

export const resolveFileName = (message = {}) => {
  if (message.file_name) {
    return message.file_name;
  }
  if (looksLikeFileName(message.text)) {
    return message.text;
  }
  return fileNameFromUrl(message.media_url);
};

export const getDocumentKind = (fileName, mimeType) => {
  const ext = getFileExtension(fileName);
  const mime = String(mimeType || "").toLowerCase();

  if (IMAGE_EXTENSIONS.has(ext) || mime.startsWith("image/")) {
    return "image";
  }
  if (VIDEO_EXTENSIONS.has(ext) || mime.startsWith("video/")) {
    return "video";
  }
  if (AUDIO_EXTENSIONS.has(ext) || mime.startsWith("audio/")) {
    return "audio";
  }
  if (PDF_EXTENSIONS.has(ext) || mime === "application/pdf") {
    return "pdf";
  }
  if (SPREADSHEET_EXTENSIONS.has(ext) || SPREADSHEET_MIMES.has(mime)) {
    return "spreadsheet";
  }
  if (WORD_EXTENSIONS.has(ext) || mime.includes("wordprocessingml") || mime === "application/msword") {
    return "word";
  }
  if (ext === "txt" || mime === "text/plain") {
    return "text";
  }
  return "document";
};

export const getDocumentTypeName = (kind, fileName) => {
  const ext = getFileExtension(fileName);

  switch (kind) {
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "pdf":
      return "PDF";
    case "spreadsheet":
      if (ext === "csv") {
        return "CSV";
      }
      if (ext === "ods") {
        return "Spreadsheet";
      }
      return "Excel";
    case "word":
      return "Word";
    case "text":
      return "Text";
    default:
      return "Document";
  }
};

export const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!value || Number.isNaN(value) || value <= 0) {
    return null;
  }
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(value < 10240 ? 1 : 0)} KB`;
  }
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

export const getFileSubtitle = (fileName, typeName, fileSize) => {
  const ext = getFileExtension(fileName);
  const extLabel = ext ? ext.toUpperCase() : String(typeName || "File").toUpperCase();
  const sizeLabel = formatFileSize(fileSize);
  return sizeLabel ? `${extLabel} • ${sizeLabel}` : extLabel;
};

export const getKindTheme = (kind) => {
  const themes = {
    image: { bg: "#E8F2FC", color: "#1677ff" },
    pdf: { bg: "#FCE8E8", color: "#cf1322" },
    spreadsheet: { bg: "#E8F8EF", color: "#08994b" },
    word: { bg: "#E8EDFC", color: "#2f54eb" },
    video: { bg: "#F3E8FC", color: "#722ed1" },
    audio: { bg: "#FFF7E6", color: "#d48806" },
    text: { bg: "#F5F5F5", color: "#595959" },
    document: { bg: "#F5F5F5", color: "#595959" },
  };
  return themes[kind] || themes.document;
};

export const resolveDocumentMeta = (message = {}) => {
  const fileName = resolveFileName(message);
  const mimeType = message.mime_type;
  const msgType = String(message.msgType || message.type || "").toLowerCase();
  let kind = getDocumentKind(fileName, mimeType);

  if (kind === "document" && ["image", "video", "audio", "sticker"].includes(msgType)) {
    kind = msgType === "sticker" ? "image" : msgType;
  }

  const typeName = getDocumentTypeName(kind, fileName);
  const label = getDisplayFileLabel(fileName, typeName);
  const fileSize = message.file_size ?? message.fileSize ?? null;
  const showExtensionOnly = Boolean(fileName && isEncodedStorageFileName(fileName));
  const sizeSuffix = formatFileSize(fileSize);
  const subtitle = showExtensionOnly
    ? getFileSubtitle(fileName, typeName, fileSize)
    : label && label !== typeName
      ? (sizeSuffix ? `${label} • ${sizeSuffix}` : label)
      : getFileSubtitle(fileName, typeName, fileSize);

  return {
    kind,
    typeName,
    fileName,
    label,
    fileSize,
    subtitle,
    displayTitle: typeName,
    showExtensionOnly,
  };
};

export const getDocumentLabel = (fileName, mimeType, kind) => {
  const resolvedKind = kind || getDocumentKind(fileName, mimeType);
  const typeName = getDocumentTypeName(resolvedKind, fileName);
  return getDisplayFileLabel(fileName, typeName);
};

export const getDocumentIcon = (kind, style = { fontSize: "3rem" }) => {
  const mergedStyle = { ...style };
  switch (kind) {
    case "image":
      return <FileImageOutlined style={mergedStyle} />;
    case "video":
      return <PlayCircleOutlined style={mergedStyle} />;
    case "audio":
      return <PlayCircleOutlined style={mergedStyle} />;
    case "pdf":
      return <FilePdfOutlined style={mergedStyle} />;
    case "spreadsheet":
      return <FileExcelOutlined style={mergedStyle} />;
    case "word":
      return <FileWordOutlined style={mergedStyle} />;
    case "text":
      return <FileTextOutlined style={mergedStyle} />;
    default:
      return <FileOutlined style={mergedStyle} />;
  }
};

export const canOpenInOnlineSpreadsheet = (kind) => kind === "spreadsheet";

export const getExcelOnlineViewerUrl = (fileUrl) =>
  `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;

export const getGoogleViewerUrl = (fileUrl) =>
  `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(fileUrl)}`;
